/**
 * AdminInvitePage — accept admin invitation token, set password, log in.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface InvitePreview {
  email: string;
  expires_at: string;
}

export default function AdminInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_BASE}/api/auth/admin-invitation/${token}`);
        const result = await response.json();
        if (!response.ok) {
          setPreviewError(result.error?.message || 'Invitation not found');
        } else {
          setPreview(result.data);
        }
      } catch (err) {
        setPreviewError(err instanceof Error ? err.message : 'Failed to load invitation');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitError('Please enter your first and last name.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/admin-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        setSubmitError(result.error?.message || 'Failed to complete registration.');
        return;
      }

      // Account created. Log in via the auth context (sets storage + state).
      if (preview?.email) {
        await login(preview.email, password);
      }
      navigate('/admin', { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to complete registration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-50 px-4">
        <p className="text-warm-500">Loading invitation…</p>
      </div>
    );
  }

  if (previewError || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-50 px-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold text-warm-900 mb-2">Invitation unavailable</h1>
          <p className="text-sm text-warm-600 mb-6">
            {previewError || 'This invitation could not be loaded. Contact your super-admin for a new invitation.'}
          </p>
          <Link to="/login" className="text-brand-600 text-sm font-medium hover:underline">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-50 px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full">
        <h1 className="text-2xl font-semibold text-warm-900 mb-2">Activate your admin account</h1>
        <p className="text-sm text-warm-600 mb-6">
          You're registering as <strong>{preview.email}</strong>. Set a password to finish setup.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-1">First name</label>
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 rounded-lg border border-warm-200 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-warm-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-warm-700 mb-1">Last name</label>
              <input
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2 rounded-lg border border-warm-200 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-warm-50"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">Password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 rounded-lg border border-warm-200 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-warm-50"
            />
            <p className="mt-1 text-xs text-warm-500">At least 8 characters.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">Confirm password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 rounded-lg border border-warm-200 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-warm-50"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-warm-600">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="rounded border-warm-300"
            />
            Show passwords
          </label>

          {submitError && (
            <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-800">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Activating…' : 'Activate Admin Account'}
          </button>

          <p className="text-xs text-warm-500 text-center">
            Invitation expires {new Date(preview.expires_at).toLocaleString()}.
          </p>
        </form>
      </div>
    </div>
  );
}
